import { Component, inject, signal, TemplateRef } from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community';
import { NbButtonModule, NbComponentType, NbIconModule, NbWindowConfig, NbWindowService } from '@nebular/theme';
import { ICellRendererAngularComp } from 'ag-grid-angular';

/** Which action buttons the column renders, and the icon each one uses. */
export interface CustomButtonRendererContext {
  isEdit?: boolean;
  isDelete?: boolean;
  isDetails?: boolean;
  editIcon?: string;
  deleteIcon?: string;
  detailsIcon?: string;
}

/** The window this column opens, plus the config it is opened with. */
export interface CustomButtonRendererView {
  windowRef: NbComponentType | TemplateRef<unknown>;
  config: Partial<Omit<NbWindowConfig, 'context'>> & { context?: CustomButtonRendererContext };
}

@Component({
  selector: 'nggc-custom-button-renderer',
  standalone: true,
  imports: [NbButtonModule, NbIconModule],
  providers: [NbWindowService],
  styleUrls: ['./custom-button.renderer.scss'],
  template: `
    @if (hasEdit()) {
      <button nbButton status="info" (click)="onEdit()"><nb-icon [icon]="editIcon()"></nb-icon></button>
    }
    @if (hasDelete()) {
      <button nbButton status="danger" (click)="onDelete()"><nb-icon [icon]="deleteIcon()"></nb-icon></button>
    }
    @if (hasDetails()) {
      <button nbButton (click)="onDetails()"><nb-icon [icon]="detailsIcon()"></nb-icon></button>
    }
  `,
})
export class CustomButtonRendererComponent implements ICellRendererAngularComp {
  onEdit() {
    this.openWindowAs({ isEdit: true, isDelete: false, isDetails: false });
  }
  onDelete() {
    this.openWindowAs({ isEdit: false, isDelete: true, isDetails: false });
  }
  onDetails() {
    this.openWindowAs({ isEdit: false, isDelete: false, isDetails: true });
  }

  /**
   * All three flags are set on every action, never just the one being taken. The column's own
   * context says which buttons this cell renders (`isEdit: true, isDelete: true, isDetails: true`
   * on the partner grid), and it is spread into the window context, so leaving a flag out hands
   * the window a request that claims to be two things at once.
   */
  private openWindowAs(mode: Required<Pick<CustomButtonRendererContext, 'isEdit' | 'isDelete' | 'isDetails'>>) {
    if (this.view?.windowRef && this.view?.config) {
      this.windowRef.open(this.view.windowRef, {
        ...this.view.config,
        context: { ...this.view.config.context, editItem: this.data, ...mode },
      });
    }
  }
  windowRef = inject(NbWindowService);
  buttonText = signal<string>('Click me!');
  view?: CustomButtonRendererView;
  data?: unknown;
  hasEdit = signal<boolean>(false);
  hasDelete = signal<boolean>(false);
  hasDetails = signal<boolean>(false);
  editIcon = signal<string>('');
  deleteIcon = signal<string>('');
  detailsIcon = signal<string>('');
  public agInit(
    params: ICellRendererParams<unknown> & { view: CustomButtonRendererView; buttonTextSufix: string },
  ): void {
    if (params.view.config.context?.isDelete) {
      this.hasDelete.set(params.view.config.context?.isDelete);
      this.deleteIcon.set(params.view.config.context?.deleteIcon ?? 'trash-2-outline');
    }
    if (params.view.config.context?.isEdit) {
      this.hasEdit.set(params.view.config.context?.isEdit);
      this.editIcon.set(params.view.config.context?.editIcon ?? 'edit-2-outline');
    }
    if (params.view.config.context?.isDetails) {
      this.hasDetails.set(params.view.config.context?.isDetails);
      this.detailsIcon.set(params.view.config.context?.detailsIcon ?? 'eye-outline');
    }
    console.log('agInit', { params });
    this.view = params.view;
    this.data = params.data;
    this.buttonText.set(params.buttonTextSufix);
  }

  public refresh(params: ICellRendererParams) {
    console.log('refresh', { params });
    return false;
  }
}
